import React, { useEffect, useMemo, useState } from "react";
import { Loader, Select } from "@mantine/core";
import { DatePickerInput } from "@mantine/dates";
import { IconChevronDown } from "@tabler/icons-react";
import dayjs from "dayjs";
import "@mantine/dates/styles.css";
import { useAuth } from "../../context/AuthContext";
import { getApiEndpoint } from "../../config/config";
import PageContent from "../shared/PageContent/PageContent";
import SubNav from "../shared/Navigation/SubNav";
import { WELCOME_SURVEY_TABS } from "./welcomeSurveyTabs";
import {
  ALL_OPTION,
  accentFilterStyles,
  filterSelectStyles,
} from "./WelcomeSurveySummary";
import { useOfficeOptions } from "./useOfficeOptions";
import { valueBand } from "./welcomeSurveyBands";
import "./WelcomeSurveyAccountQualityPage.css";

// The window the report opens on, matching the endpoint's own default of the
// last seven days — the same default the Report and Rep Quality tabs use, so
// switching tabs doesn't silently change the period the figures cover.
function defaultDateRange() {
  return [
    dayjs().subtract(7, "day").format("YYYY-MM-DD"),
    dayjs().format("YYYY-MM-DD"),
  ];
}

// FieldRoutes gave these accounts no service type. They are real sales and are
// counted like any other, they just have no name to group under — so the row
// says that rather than sitting blank at the top of the column.
const UNTYPED_LABEL = "No service type";

function subscriptionLabel(name) {
  return name ?? UNTYPED_LABEL;
}

// Percentages come back to two decimal places; the table shows whole numbers,
// so a column of rates lines up as a column rather than as ragged decimals.
function formatPercent(value) {
  const number = Number(value);
  return Number.isFinite(number) ? `${Math.round(number)}%` : "—";
}

function formatCount(value) {
  const number = Number(value);
  return Number.isFinite(number) ? number.toLocaleString() : "—";
}

// Annual contract value, to the dollar. The cents the endpoint reports are
// noise next to six-figure revenue totals, and dropping them is what lets the
// four money columns line up.
function formatMoney(value) {
  const number = Number(value);
  if (!Number.isFinite(number)) return "—";
  return number.toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  });
}

/// The board's columns, in the order they are drawn.
const COLUMNS = [
  { key: "retention", label: "Retention" },
  { key: "total_serviced_revenue", label: "Total Sold Revenue" },
  { key: "retained_revenue", label: "Retained Revenue" },
  { key: "avg_acv", label: "Avg ACV" },
  { key: "avg_arv", label: "Avg ARV" },
  { key: "avg_account_life_days", label: "Avg Account Life" },
];

/// The four headline figures: the type that earned the most, the type that kept
/// the most, the type that lost the most, and what the window is still earning
/// altogether.
///
/// The first three each name a type over the figure that won it that title, so
/// the value line is a name and the note under it is the evidence. Total active
/// revenue is the odd one out and leads with the money, because there is no type
/// to name — it is every type added up, and so it carries no note either: there
/// would be nothing under it but the label restated.
///
/// None of the four is banded. A tile names the best or worst type on its own
/// measure, so its figure is already the extreme of its column — colouring it
/// would only restate the title above it. The banding on this report belongs to
/// the retention column, where the rates have each other to be read against.
function summaryCards(data) {
  const grossing = data?.highest_grossing_subscription;
  const retention = data?.highest_retention_subscription;
  const cancelled = data?.most_cancelled_subscription;

  return [
    {
      label: "Highest Grossing Subscription",
      value: grossing ? subscriptionLabel(grossing.subscription_name) : "—",
      note: grossing ? `${formatMoney(grossing.revenue)} annual` : "No sales",
    },
    {
      label: "Highest Retention",
      value: retention ? subscriptionLabel(retention.subscription_name) : "—",
      note: retention
        ? `${formatPercent(retention.retention_percentage)} retained`
        : "No sales",
    },
    {
      // Null here is the good case rather than a missing figure: nothing in the
      // window cancelled, so there is no worst type to name. The note says so
      // outright instead of leaving a dash to be read as an error.
      label: "Most Cancels",
      value: cancelled ? subscriptionLabel(cancelled.subscription_name) : "—",
      note: cancelled
        ? `${formatCount(cancelled.cancelled_count)} accounts cancelled`
        : "Nothing cancelled",
    },
    {
      label: "Total Active Revenue",
      value: formatMoney(data?.total_active_revenue),
    },
  ];
}

/**
 * The Account Quality tab: every subscription type the welcome-survey reps sold
 * inside the reporting window, with what became of those accounts and what they
 * were worth, by revenue, largest first. Its own page rather than a panel on the Report tab, because it
 * comes from its own endpoint — report-account-quality groups the window's
 * accounts by the service they were sold on, so this reads the same window as
 * the Rep Quality tab cut the other way: by product rather than by seller.
 */
function WelcomeSurveyAccountQualityPage() {
  const { getVerifiedJWT } = useAuth();
  const [dateRange, setDateRange] = useState(defaultDateRange);
  const [officeId, setOfficeId] = useState(ALL_OPTION);
  const officeOptions = useOfficeOptions();
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const [startDate, endDate] = dateRange ?? [null, null];

    // Mid-selection in the range picker — wait for the second date rather than
    // firing a request for a half-picked window.
    if (startDate && !endDate) return undefined;

    const loadReport = async () => {
      setLoading(true);
      setError(null);
      try {
        const jwt = await getVerifiedJWT();
        if (!jwt) throw new Error("Authentication failed");

        const response = await fetch(
          getApiEndpoint("/v2/welcome-survey/report-account-quality"),
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${jwt}`,
            },
            body: JSON.stringify({
              office_id:
                !officeId || officeId === ALL_OPTION ? null : Number(officeId),
              start_date: startDate ?? null,
              end_date: endDate ?? null,
            }),
          }
        );

        if (!response.ok)
          throw new Error("Failed to load the account quality report");

        const result = await response.json();
        if (cancelled) return;
        setReport(result?.data ?? result);
      } catch (err) {
        if (cancelled) return;
        setError(err.message || "Failed to load the account quality report");
        setReport(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    loadReport();
    return () => {
      cancelled = true;
    };
  }, [getVerifiedJWT, dateRange, officeId]);

  // Revenue, largest first — the order the endpoint sends, drawn as sent. The
  // table is the report rather than a re-ranking of it.
  const subscriptions = report?.subscriptions ?? [];

  const cards = useMemo(() => summaryCards(report), [report]);

  return (
    <PageContent
      customHeader={
        <SubNav
          title="Welcome Survey"
          tabs={WELCOME_SURVEY_TABS}
          activeId="account-quality"
          ariaLabel="Welcome survey sections"
        />
      }
    >
      <div className="ws-aqp">
        <div className="ws-aqp-header">
          <div className="ws-aqp-header-ident">
            <h2 className="ws-aqp-title">
              Account Quality
              {loading && <Loader size="xs" ml="sm" display="inline-block" />}
            </h2>
            <p className="ws-aqp-caption">
              Retention and revenue quality by subscription type.
            </p>
          </div>
          {/* Both narrow the accounts the whole report is built from, so they
              move the headline tiles as well as the rows — which is why they
              sit up here over the tiles rather than down on the table. */}
          <div className="ws-aqp-header-controls">
            <Select
              data={officeOptions}
              value={officeId}
              onChange={value => setOfficeId(value ?? ALL_OPTION)}
              allowDeselect={false}
              radius="xl"
              aria-label="Filter by office"
              styles={filterSelectStyles}
            />
            <DatePickerInput
              type="range"
              value={dateRange}
              onChange={setDateRange}
              valueFormat="MMM D, YYYY"
              allowSingleDateInRange
              radius="xl"
              aria-label="Reporting window"
              placeholder="Pick dates"
              rightSection={<IconChevronDown size={16} />}
              rightSectionPointerEvents="none"
              styles={accentFilterStyles}
            />
          </div>
        </div>

        <div className="ws-aqp-cards">
          {/* Until a report lands there is nothing to name, so the tiles hold
              their shape on dashes rather than reading as a window in which
              nothing sold. */}
          {cards.map(card => (
            <div className="ws-aqp-card" key={card.label}>
              <div className="ws-aqp-card-label">{card.label}</div>
              <div className="ws-aqp-card-value">
                {report && !error ? card.value : "—"}
              </div>
              {/* Only the three tiles that name a type carry evidence under
                  the name; the revenue total is its own evidence. */}
              {card.note && (
                <div className="ws-aqp-card-note">
                  {error ? "Unavailable" : report ? card.note : "—"}
                </div>
              )}
            </div>
          ))}
        </div>

        <section className="ws-aqp-board">
          <div className="ws-aqp-board-header">
            <h3 className="ws-aqp-title">Breakdown by Subscription Type</h3>
          </div>

          <div className="ws-aqp-table-scroll">
            <table className="ws-aqp-table">
              <thead>
                <tr>
                  <th scope="col" className="ws-aqp-name-col">
                    Subscription
                  </th>
                  {COLUMNS.map(column => (
                    <th key={column.key} scope="col" className="ws-aqp-metric">
                      {column.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {subscriptions.map(row => (
                  <SubscriptionRow
                    key={row.subscription_name ?? UNTYPED_LABEL}
                    row={row}
                  />
                ))}
              </tbody>
            </table>

            {subscriptions.length === 0 && (
              <div className="ws-aqp-empty">
                {loading
                  ? "Loading subscriptions…"
                  : error
                    ? error
                    : "No account was sold in this window."}
              </div>
            )}
          </div>
        </section>
      </div>
    </PageContent>
  );
}

/// One subscription type. Retention leads with a gauge beside its rate, so the
/// column reads as a set of filled bars at a glance; the money columns beside
/// it stay plain, because a large number there is neither good nor bad on its
/// own — it is the retention next to it that says which.
///
/// Avg ACV and Avg ARV each carry a second figure under the first: the average
/// contract is the initial plus what recurs, so the parts sit under the totals
/// they make up rather than in two more columns of their own.
function SubscriptionRow({ row }) {
  const name = subscriptionLabel(row.subscription_name);
  const retention = Number(row.retention_percentage);
  const band = valueBand(retention);
  // Every account of this type, not just the lost ones: a cancelled account
  // is measured to its cancellation, a running one as far as it has got.
  const lifeDays = row.avg_account_life_days;
  const hasLifeDays = lifeDays !== null && lifeDays !== undefined;

  return (
    <tr className="ws-aqp-row">
      <td className="ws-aqp-name-col">
        <div className="ws-aqp-name" title={name}>
          {name}
        </div>
      </td>

      <td className="ws-aqp-metric">
        <div className="ws-aqp-retention">
          <span className="ws-aqp-bar">
            <span
              className={`ws-aqp-bar-fill ws-aqp-bar-${band}`}
              style={{
                width: `${Math.min(Math.max(retention || 0, 0), 100)}%`,
              }}
            />
          </span>
          <span className={`ws-aqp-figure ws-aqp-tone-${band}`}>
            {formatPercent(retention)}
          </span>
        </div>
      </td>

      <td className="ws-aqp-metric">
        <span className="ws-aqp-figure">
          {formatMoney(row.total_serviced_revenue)}
        </span>
      </td>

      <td className="ws-aqp-metric">
        <span className="ws-aqp-figure">
          {formatMoney(row.retained_revenue)}
        </span>
      </td>

      <td className="ws-aqp-metric">
        <div className="ws-aqp-stack">
          <span className="ws-aqp-figure">{formatMoney(row.avg_acv)}</span>
          <span className="ws-aqp-subfigure">
            Avg Initial {formatMoney(row.avg_initial)}
          </span>
        </div>
      </td>

      <td className="ws-aqp-metric">
        <div className="ws-aqp-stack">
          <span className="ws-aqp-figure">{formatMoney(row.avg_arv)}</span>
          <span className="ws-aqp-subfigure">
            Avg Recurring {formatMoney(row.avg_recurring)}
          </span>
        </div>
      </td>

      <td className="ws-aqp-metric">
        {hasLifeDays ? (
          <span className="ws-aqp-figure">
            {formatCount(Math.round(lifeDays))}{" "}
            <span className="ws-aqp-unit">days</span>
          </span>
        ) : (
          // FieldRoutes holds no dates for any account of this type, so there
          // is no span to average. A dash rather than a zero: a zero would read
          // as accounts that died the day they were sold.
          <span
            className="ws-aqp-figure ws-aqp-unknown"
            title="No account of this type has the dates on record to measure"
          >
            —
          </span>
        )}
      </td>
    </tr>
  );
}

export default WelcomeSurveyAccountQualityPage;
